import { ReactNode } from "react";
import { STEPS } from "../../data/steps";
import { usePumpSizing } from "../../context/PumpSizingContext";
import { useScrollToTopOnStepChange } from "../../hooks/useScrollToTopOnStepChange";
import { getStepSelectedValue } from "../../utils/helpers/helpers";
import { StepIndicator } from "./StepIndicator";
import { SavedResultIdBadge } from "../steps/pumpcurve/SavedResultIdBadge";
import { StartAgainButton } from "../steps/pumpcurve/StartAgainButton";

type LayoutProps = {
	title: string;
	description: string;
	children: ReactNode;
	footer?: ReactNode;
	/** Shown above the step content, typically a compact copy of the nav buttons. */
	topNav?: ReactNode;
};

export function Layout({ title, description, children, footer, topNav }: LayoutProps) {
	const { currentStep, formData, savedSubmissionUuid } = usePumpSizing();
	const layoutRef = useScrollToTopOnStepChange<HTMLDivElement>(currentStep);

	return (
		<div className="pf-layout">
			<header className="pf-layout__header">
				<h1 className="pf-layout__title">{title}</h1>
				<p className="pf-layout__description">{description}</p>
				{savedSubmissionUuid ? (
					<SavedResultIdBadge
						className="pf-layout__saved-id"
						action={<StartAgainButton />}
					/>
				) : null}
			</header>

			<div className="pf-layout__body" ref={layoutRef}>
				<aside className="pf-layout__sidebar">
					<nav className="pf-layout__steps" aria-label="Pump sizing steps">
						{STEPS.map((step) => {
							const isCompleted = currentStep > step.id;
							return (
								<StepIndicator
									key={step.id}
									stepNumber={step.id}
									label={step.label}
									isActive={currentStep === step.id}
									isCompleted={isCompleted}
									selectedValue={
										isCompleted
											? getStepSelectedValue(step.id, formData)
											: undefined
									}
								/>
							);
						})}
					</nav>
				</aside>

				<main className="pf-layout__content">
					{topNav ? (
						<div className="pf-layout__top-nav">{topNav}</div>
					) : null}
					{children}
					{footer ? <footer className="pf-layout__footer">{footer}</footer> : null}
				</main>
			</div>
		</div>
	);
}
